import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "slick-carousel/slick/slick.css";
import OTPVerification from './pages/Signup/OTPVerification';
import "slick-carousel/slick/slick-theme.css";
import axios from "axios";
import Blog from "./components/Blog/Blog.js";
import Header from "./components/header/Header";
import Wishlist from "./pages/watchlist/watchlist";
import OrderList from "./pages/Checkout/Orderlist";

import Footer from "./components/footer/footer";
import Home from "./pages/Home/index";
import About from "./pages/About";
import Listing from "./pages/Listing";
import Detailspage from "./pages/Details";
import SignUp from "./pages/Signup";

import Checkout from "./pages/Checkout/Checkout";
import SearchPage from "./pages/search";
import Notfound from "./pages/Notfound";
import LoginForm from "./pages/Signin";
import Cart from "./pages/cart";

// ADD THIS IMPORT FOR EMAIL VERIFICATION
import EmailVerification from "./pages/Signup/EmailVerification";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { fetchDataFromApi, postData } from "./utils/api";
import ContactForm from "./components/contect/ContactForm";


import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";

export const MyContext = createContext();

// Progress Bar Component
const ProgressBar = ({ isLoading }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setProgress(0);
      const timer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(timer);
            return 90;
          }
          return prev + Math.random() * 30;
        });
      }, 100);

      return () => clearInterval(timer);
    } else {
      // Complete the progress bar
      setProgress(100);
      const timer = setTimeout(() => {
        setProgress(0);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  if (!isLoading && progress === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        backgroundColor: '#f0f0f0',
        zIndex: 9999,
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          height: '100%',
          backgroundColor: '#28a745',
          width: `${progress}%`,
          transition: 'width 0.3s ease',
          boxShadow: '0 0 10px rgba(40, 167, 69, 0.5)'
        }}
      />
    </div>
  );
};

// Route Change Handler Component
const RouteChangeHandler = ({ setPageLoading }) => {
  const location = useLocation();

  useEffect(() => {
    setPageLoading(true);

    // Remove simulated delay to let skeletons show immediately
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 10);

    return () => clearTimeout(timer);
  }, [location.pathname, setPageLoading]);

  return null;
};

function App() {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [isLoading, setIsLoading] = useState(false);
  const [isLogin, setIsLoging] = useState(false);
  const [isAddingInCart, setIsAddingInCart] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [user, setUser] = useState(null);
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [pageLoading, setPageLoading] = useState(false); // New state for page loading

  const [alertBox, setAlertBox] = useState({
    open: false,
    msg: "",
    error: false,
  });

  // Helper function to get user from localStorage
  const getCurrentUser = useCallback(() => {
    try {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }, []);

  // Update login state and user data
  const updateLoginState = useCallback(() => {
    const userData = getCurrentUser();
    const token = localStorage.getItem("token");



    setUser(userData);
    setIsLoging(!!(userData && token));
  }, [getCurrentUser]);

  // Check login state on mount and when localStorage changes
  useEffect(() => {
    updateLoginState();

    // Listen for storage changes (login/logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'token') {
        console.log("Storage changed:", e.key, e.newValue);
        updateLoginState();
      }
    };

    // Also check periodically for login state changes (for Google login)
    const intervalId = setInterval(() => {
      const currentUserData = getCurrentUser();
      const currentToken = localStorage.getItem("token");
      const shouldBeLoggedIn = !!(currentUserData && currentToken);

      if (shouldBeLoggedIn !== isLogin) {
        console.log("Login state mismatch detected, updating...");
        updateLoginState();
      }
    }, 1000); // Check every second

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [updateLoginState, isLogin, getCurrentUser]);

  const getcountry = useCallback(async (url) => {
    try {
      const res = await axios.get(url);
      if (res && res.data && Array.isArray(res.data.data)) {
        setCountryList(
          res.data.data.map((item) => ({
            label: item.country,
            value: item.country,
          }))
        );
      }
    } catch (error) {
      console.log("Error fetching countries:", error.message);
    }
  }, []);

  const getWatchlistData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const currentUser = getCurrentUser();
    const userId = currentUser?._id || currentUser?.id || currentUser?.userId;

    if (!token || !userId) {
      setWatchlistItems([]);
      return;
    }

    try {
      const response = await fetchDataFromApi(`/api/watchlist/${userId}`);
      if (response?.success) {
        setWatchlistItems(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching watchlist:", error);
      setWatchlistItems([]);
    }
  }, [getCurrentUser]);

  const refreshWatchlist = useCallback(() => {
    getWatchlistData();
  }, [getWatchlistData]);

  const getCartData = useCallback(async () => {
    const currentUser = getCurrentUser();

    if (!currentUser?.userId) {
      setCartItems([]);
      return;
    }

    try {
      const res = await fetchDataFromApi(
        `/api/cart?userId=${currentUser.userId}`
      );
      console.log("Cart data fetched:", res);

      let items = [];
      if (Array.isArray(res)) {
        items = res;
      } else if (res && Array.isArray(res.data)) {
        items = res.data;
      } else if (res && Array.isArray(res.cartItems)) {
        items = res.cartItems;
      } else if (res && res.success && Array.isArray(res.result)) {
        items = res.result;
      }

      setCartItems(items);
    } catch (error) {
      console.error("Error fetching cart data:", error);
      setCartItems([]);
    }
  }, [getCurrentUser]);

  const addToCart = useCallback(
    async (items) => {
      console.log("AddToCart called with:", items);

      const currentUser = getCurrentUser();
      const token = localStorage.getItem("token");

      if (!currentUser?.userId || !token) {
        setAlertBox({
          open: true,
          error: true,
          msg: "Please Login first",
        });
        return false;
      }

      if (
        !items.productId ||
        !items.productTitle ||
        !items.price ||
        !items.quantity
      ) {
        console.error("Missing required cart item fields:", items);
        setAlertBox({
          open: true,
          error: true,
          msg: "Invalid product data. Please try again.",
        });
        return false;
      }

      setIsAddingInCart(true);
      setIsLoading(true);

      try {
        const cartData = {
          ...items,
          price: parseFloat(items.price),
          quantity: parseInt(items.quantity),
          subTotal: parseFloat(items.price) * parseInt(items.quantity),
          userId: currentUser.userId,
        };

        console.log("Sending cart data to API:", cartData);

        const response = await postData(`/api/cart/add`, cartData);
        console.log("API response:", response);

        if (response && response.status !== false) {
          setAlertBox({
            open: true,
            error: false,
            msg: "Item added to cart successfully!",
          });
          await getCartData();
          return true;
        } else {
          setAlertBox({
            open: true,
            error: true,
            msg: response?.msg || "Failed to add item to cart",
          });
          return false;
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        setAlertBox({
          open: true,
          error: true,
          msg: "Something went wrong. Please try again.",
        });
        return false;
      } finally {
        setIsLoading(false);
        setIsAddingInCart(false);
      }
    },
    [getCurrentUser, getCartData]
  );

  const emptyCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const selectCountry = useCallback((country) => {
    setSelectedCountry(country);
    localStorage.setItem("location", country);
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setAlertBox((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      try {
        const res = await fetchDataFromApi("/api/category");

        if (!isMounted) return;

        if (res && Array.isArray(res.categoryList)) {
          setCategories(res.categoryList);

          const subCatArr = [];
          res.categoryList.forEach((cat) => {
            if (cat?.children?.length) {
              cat.children.forEach((subCat) => subCatArr.push(subCat));
            }
          });
          setSubCategories(subCatArr);
        } else {
          setCategories([]);
          setSubCategories([]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        if (isMounted) {
          setCategories([]);
          setSubCategories([]);
        }
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    getcountry("https://countriesnow.space/api/v0.1/countries/");
  }, [getcountry]);

  useEffect(() => {
    const location = localStorage.getItem("location");
    if (location) {
      setSelectedCountry(location);
    } else {
      setSelectedCountry("All");
      localStorage.setItem("location", "All");
    }
  }, []);

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Update cart and watchlist when login state changes
  useEffect(() => {
    if (isLogin && user?.userId) {
      getCartData();
      getWatchlistData();
    } else {
      setCartItems([]);
      setWatchlistItems([]);
    }
  }, [isLogin, user?.userId, getCartData, getWatchlistData]);

  const contextValue = useMemo(
    () => ({
      categories,
      setCategories,
      subCategories,
      setSubCategories,
      loadingCategories,
      windowWidth,
      selectedCountry,
      setSelectedCountry,
      selectCountry,
      alertBox,
      setAlertBox,
      isLoading,
      setIsLoading,
      addToCart,
      isLogin,
      setIsLoging,
      getCartData,
      isAddingInCart,
      setIsAddingInCart,
      emptyCart,
      cartItems,
      setCartItems,
      countryList,
      setCountryList,
      watchlistItems,
      setWatchlistItems,
      getWatchlistData,
      refreshWatchlist,
      user,
      updateLoginState,
      getCurrentUser,
      pageLoading, // Add pageLoading to context
      setPageLoading, // Add setPageLoading to context
    }),
    [
      categories,
      subCategories,
      loadingCategories,
      windowWidth,
      selectedCountry,
      selectCountry,
      alertBox,
      isLoading,
      addToCart,
      isLogin,
      getCartData,
      isAddingInCart,
      emptyCart,
      cartItems,
      countryList,
      watchlistItems,
      getWatchlistData,
      refreshWatchlist,
      user,
      updateLoginState,
      getCurrentUser,
      pageLoading,
    ]
  );

  return (
    <MyContext.Provider value={contextValue}>
      <BrowserRouter>
        <ScrollToTop />
        {/* Progress Bar */}
        <ProgressBar isLoading={pageLoading} />

        {/* Route Change Handler */}
        <RouteChangeHandler setPageLoading={setPageLoading} />

        <Snackbar
          open={alertBox.open}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleClose}
            severity={alertBox.error === true ? "error" : "success"}
            variant="filled"
            sx={{
              width: "100%",
              backgroundColor: alertBox.error ? "#e74c3c" : "#28a745", // Matches Category Button Green
              color: "#ffffff !important",
              fontWeight: "600",
              borderRadius: "10px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
              "& .MuiAlert-icon": {
                color: "#ffffff"
              }
            }}
          >
            {alertBox.msg}
          </Alert>
        </Snackbar>

        <Header />
        <Routes>
          <Route exact path="/" element={<Home />} />
          <Route exact path="/about" element={<About />} />
          <Route exact path="/listing" element={<Listing />} />
          <Route exact path="/details/:id" element={<Detailspage />} />
          <Route exact path="/signup" element={<SignUp />} />
          <Route exact path="/login" element={<LoginForm />} />

          <Route path="/verify-otp" element={<OTPVerification />} />
          {/* ADD EMAIL VERIFICATION ROUTE */}
          <Route exact path="/verify-email/:token" element={<EmailVerification />} />

          <Route exact path="/Checkout" element={<Checkout />} />
          <Route exact path="/cart" element={<Cart />} />
          <Route exact path="/contacts" element={<ContactForm />} />
          <Route exact path="/orders" element={<OrderList />} />
          <Route path="/watchlist" element={<Wishlist />} />
          <Route exact path="/search" element={<SearchPage />} />
          <Route exact path="/Blog" element={<Blog />} />

          <Route path="*" element={<Notfound />} />
        </Routes>
        <Footer />
        <ScrollToTopButton />


      </BrowserRouter>
    </MyContext.Provider>
  );
}

export default App;